// Original single-stroke icon set for the Graphy surface. One consistent 24-grid,
// 1.75 stroke, round caps/joins, currentColor — so icons take the text color and
// stroke weight of wherever they sit. No emoji or glyph stand-ins anywhere in the UI.

type IconProps = { size?: number; className?: string };

function svg(path: React.ReactNode, size: number, className?: string) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {path}
    </svg>
  );
}

export const Icon = {
  overview: ({ size = 16, className }: IconProps) =>
    svg(
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M12 4v2M12 18v2M4 12h2M18 12h2M6.3 6.3l1.4 1.4M16.3 16.3l1.4 1.4M17.7 6.3l-1.4 1.4M7.7 16.3l-1.4 1.4" />
      </>,
      size,
      className,
    ),
  map: ({ size = 16, className }: IconProps) =>
    svg(
      <>
        <circle cx="6" cy="6" r="2.2" />
        <circle cx="18" cy="8" r="2.2" />
        <circle cx="9" cy="18" r="2.2" />
        <path d="M8 7l8 0.6M7.4 8l1.2 8M10.9 17l5.5-7" />
      </>,
      size,
      className,
    ),
  explore: ({ size = 16, className }: IconProps) =>
    svg(
      <>
        <circle cx="12" cy="12" r="2.3" />
        <circle cx="12" cy="4.5" r="1.8" />
        <circle cx="5" cy="18" r="1.8" />
        <circle cx="19" cy="18" r="1.8" />
        <path d="M12 6.3v3.4M10.4 13.4L6.3 16.6M13.6 13.4l4.1 3.2" />
      </>,
      size,
      className,
    ),
  tools: ({ size = 16, className }: IconProps) =>
    svg(
      <path d="M14.7 6.3a3.7 3.7 0 00-5 4.9l-5.4 5.3a1.6 1.6 0 002.2 2.2l5.3-5.3a3.7 3.7 0 004.9-5l-2.3 2.3-2.1-.6-.6-2.1 2.3-2.3z" />,
      size,
      className,
    ),
  contributors: ({ size = 16, className }: IconProps) =>
    svg(
      <>
        <circle cx="9" cy="8" r="3" />
        <path d="M3.5 19a5.5 5.5 0 0111 0" />
        <path d="M16 6.2a3 3 0 010 5.6M17.5 14.4a5.5 5.5 0 013 4.6" />
      </>,
      size,
      className,
    ),
  wiki: ({ size = 16, className }: IconProps) =>
    svg(
      <>
        <path d="M4 5.5A1.5 1.5 0 015.5 4H11v15H5.5A1.5 1.5 0 014 17.5z" />
        <path d="M20 5.5A1.5 1.5 0 0018.5 4H13v15h5.5a1.5 1.5 0 001.5-1.5z" />
      </>,
      size,
      className,
    ),
  reviews: ({ size = 16, className }: IconProps) =>
    svg(
      <>
        <circle cx="6" cy="6" r="2.2" />
        <circle cx="6" cy="18" r="2.2" />
        <circle cx="18" cy="18" r="2.2" />
        <path d="M6 8.2v7.6M18 15.8v-4a3 3 0 00-3-3h-4.5M12.5 6.2L10 8.7l2.5 2.5" />
      </>,
      size,
      className,
    ),
  bell: ({ size = 16, className }: IconProps) =>
    svg(
      <>
        <path d="M6 9a6 6 0 0112 0c0 5 1.5 6 2 7H4c.5-1 2-2 2-7z" />
        <path d="M10 20a2 2 0 004 0" />
      </>,
      size,
      className,
    ),
  search: ({ size = 16, className }: IconProps) =>
    svg(
      <>
        <circle cx="11" cy="11" r="6" />
        <path d="M20 20l-3.5-3.5" />
      </>,
      size,
      className,
    ),
  source: ({ size = 16, className }: IconProps) =>
    svg(<path d="M9 8l-4 4 4 4M15 8l4 4-4 4M13 5l-2 14" />, size, className),
  ask: ({ size = 16, className }: IconProps) =>
    svg(
      <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6zM18.5 15l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8z" />,
      size,
      className,
    ),
  plus: ({ size = 16, className }: IconProps) => svg(<path d="M12 5v14M5 12h14" />, size, className),
  minus: ({ size = 16, className }: IconProps) => svg(<path d="M5 12h14" />, size, className),
  reset: ({ size = 16, className }: IconProps) =>
    svg(<path d="M4 9a8 8 0 1 1-1 5M4 5v4h4" />, size, className),
  chevron: ({ size = 16, className }: IconProps) => svg(<path d="M9 6l6 6-6 6" />, size, className),
  fit: ({ size = 16, className }: IconProps) =>
    svg(<path d="M4 9V5a1 1 0 0 1 1-1h4M15 4h4a1 1 0 0 1 1 1v4M20 15v4a1 1 0 0 1-1 1h-4M9 20H5a1 1 0 0 1-1-1v-4" />, size, className),
  expand: ({ size = 16, className }: IconProps) =>
    svg(<path d="M14 4h6v6M10 20H4v-6M20 4l-7 7M4 20l7-7" />, size, className),
  shrink: ({ size = 16, className }: IconProps) =>
    svg(<path d="M20 10h-6V4M4 14h6v6M14 10l7-7M10 14l-7 7" />, size, className),
  relayout: ({ size = 16, className }: IconProps) =>
    svg(
      <>
        <circle cx="6" cy="7" r="2" />
        <circle cx="18" cy="6" r="2" />
        <circle cx="12" cy="18" r="2" />
        <path d="M8 7.6l8-1.2M7.2 8.8l3.8 7.4M17 7.9l-4 8.3" />
      </>,
      size,
      className,
    ),
  pin: ({ size = 16, className }: IconProps) =>
    svg(<path d="M9 4h6l-1 5 3 3H7l3-3-1-5zM12 15v5" />, size, className),
  dot: ({ size = 16, className }: IconProps) => svg(<circle cx="12" cy="12" r="4" />, size, className),
  // Brand mark: three linked nodes forming a hammer-head triangle. Original.
  brand: ({ size = 20, className }: IconProps) =>
    svg(
      <>
        <circle cx="12" cy="5" r="2.1" />
        <circle cx="5" cy="17" r="2.1" />
        <circle cx="19" cy="17" r="2.1" />
        <path d="M12 7.1L6.2 15.3M12 7.1l5.8 8.2M7.1 17h9.8" />
      </>,
      size,
      className,
    ),
};

export type IconName = keyof typeof Icon;
